import { Helmet } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { RenewalFilterProvider, RenewalsFilterMenu, RenewalsFilterTags, useRenewalsFilter } from "../components/filter"
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from "@heroicons/react/20/solid"
import { ArrowRightStartOnRectangleIcon, RocketLaunchIcon } from "@heroicons/react/24/solid"
import { useMemo } from "react"
import { useFilteredPatents } from "src/filter/FilteredPatents"
import { useClaimScopes } from "src/products/products"
import { SortButton } from "src/patents/datawizard/DataWizard"
import { Outlet } from "react-router-dom"
import { ClaimScopeHeader, FamilyFooter, FamilyHeader, TableCard } from "../components/TablePieces"
import { Link } from "src/navigation"
import { Member } from "src/patents/patents"
import clsx from "clsx"
import { IconSpinner } from "src/components/icons"
import { useAugmentRenewal, useRenewals } from "../interfaces/Renwals"
import { createClaimScopeGroups } from "../dennemeyer/utils"
import _ from "lodash"
import { AugmentedRenewal, DueDateStatus, Renewal, RenewalState } from "../interfaces/renewal_types"
import { memberUrl } from "src/patents/utils"
import { ExpiryRibbon, MemberRibbons } from "../dennemeyer/components"
import { FeeAmount, InstructionDueDate, StatusView } from "../components/InstructionOverview"
import { canBeInstructed  } from "../components/dueDates"
import RenewalsProvider from "../interfaces/RenewalsProvider"
import { BulkEditRow } from "../components/BulkInstruction"
import { useSettings } from "../interfaces/Settings"
import { capitalize } from "src/utils/strings"
import { useHints } from "../components/Hints"

export default function RenewalsInstructionPage() {
    const {t} = useTranslation()
    const {implementation} = useSettings()

    const {hint, isHintNeeded} = useHints()

    return <>
        {/* @ts-ignore */}
        <Helmet>
            {/* <title>{t('undecided-maintenances')} | {t('renewal-fees')} | Patent Cockpit</title> */}
            <title>{t('renewal-notices')} | {t('renewal-fees')} | Patent Cockpit</title>
        </Helmet>

        <RenewalFilterProvider>
            <div className="header-row pb-1 flex flex-row gap-1 items-center max-w-6xl xl:max-w-7xl pr-2">
                {/* <h2 className="modern-h2 mr-auto">{t('undecided-maintenances')}</h2> */}
                <h2 className="modern-h2 mr-auto" title={`Provider: ${capitalize(implementation)}`}>{t('renewal-notices')}</h2>
                <RenewalsFilterTags />
                <RenewalsFilterMenu />
            </div>
            <div className="px-2 pb-6 max-w-6xl xl:max-w-7xl">
                {isHintNeeded ? hint :
                <RenewalsTableContext />}
            </div>
        </RenewalFilterProvider>
    </>
}

function RenewalsTableContext() {
    const {minDate, maxDate, customRange} = useRenewalsFilter()
    const states = ['Open'] satisfies RenewalState[]
    const start = customRange ? minDate : undefined
    const end = customRange ? maxDate : undefined
    return (
        <RenewalsProvider {...{states, start, end}}>
            <RenewalsTable />
        </RenewalsProvider>
    )
}

function RenewalsTable() {
    const {t} = useTranslation()
    //const {settings} = useDmSettings()
    //const startDate = settings?.startDate
    const startDate = '2023-01-01'
    //const {firstPossibleFeeDueDate, latestPossibleFeeDueDate} = useTimeline()

    const {
        onlyUndecided, groupBy,
        sortBy, setSortBy,
        sortOrder, setSortOrder,
        customRange, minDate, maxDate,
    } = useRenewalsFilter()

    const {renewals, isLoading} = useRenewals()
    const {augmentRenewal} = useAugmentRenewal()
    // const isLoading = false // TODO
    // console.log({renewals })

    // const {augmentMaintenanceAction} = useAugmentMaintenanceAction(!onlyUndecided)
    const {familyById} = useFilteredPatents(!onlyUndecided)
    const {claimScopeIdsByMemberId} = useClaimScopes()

    const sortByFunction = sortBy === 'internalReference' 
        ? (m: AugmentedRenewal) => m.member.internalReference 
        : (m: Renewal) => m.feeDueDate

    const groupFunction 
        = groupBy === 'by-patent-family' || groupBy === 'by-claim-scopes' 
        ? (m: {member: Member}) => m.member.patentFamilyId
        : (m: {member: Member}) => '' 

    const sortGroup: (arg : [key: string, values: AugmentedRenewal[]]) => string = 
        groupBy === 'by-patent-family'
            ? (sortBy === 'internalReference'
                ? ([key, values]) => familyById[key]?.internalReference ?? key
                : ([key, values]) => _(values).map('DueDate').min()) // or max ?
        : groupBy === 'by-claim-scopes'
            ? (sortBy === 'internalReference'
                ? ([key, values]) => familyById[values[0]?.member?.familyMemberId]?.internalReference ?? key
                : ([key, values]) => _(values).map('DueDate').min()) // or max ?
            : ([key, values]) => key

    const filterRow = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10)
        const filters = [
            (m: AugmentedRenewal) => (m.member !== undefined && m.member.patentFamilyId !== undefined && m.feeDueDate >= today),
            startDate && ((m: AugmentedRenewal) => m.instructionDueDate >= startDate),
            //onlyUndecided && ((m: AugmentedRenewal) => isUndecided(m, m.instruction, hardInstructionCuttoff)),
            onlyUndecided && ((m: AugmentedRenewal) => m.instruction === undefined), // isUndecided(m, m.instruction, hardInstructionCuttoff)),
            customRange && ((m: AugmentedRenewal) => minDate <= m.instructionDueDate && m.instructionDueDate <= maxDate),
        ].filter(Boolean)
        return (m: AugmentedRenewal) => {
            // console.log({m})
            // console.log(filters.map(f => f(m)))
            return filters.every(f => f(m))
        }
    }, [onlyUndecided, customRange, minDate, maxDate, startDate])

    const headers = [
        {key: 'patent', label: t('patent'),
            button: <SortButton {...{searchField: 'internalReference', sortField: sortBy, sortOrder, setSortField: setSortBy, setSortOrder}}/>},
        {key: 'due', label: t('due'),
            button: <SortButton {...{searchField: 'due', sortField: sortBy, sortOrder, setSortField: setSortBy, setSortOrder}}/>},
        {key: 'fees', label: t('fees') },
        { key: 'instruction', label: '', className: 'text-right', button: groupBy !== 'by-family-member' && <ToggleDetailsView /> },
    ]

    //const maintenanceActions = _(data?.pages?.flatMap(d => d.Data?.Page ?? []) ?? [])
    const maintenanceActions = _(renewals)
        .filter(Boolean)
        .map(augmentRenewal)
        .filter(filterRow)
        .groupBy(groupFunction)
        .mapValues(vs => _(vs)
            .sortBy(sortByFunction)
            .tap(ms => sortOrder > 0 ? ms : _.reverse(ms))
            .value())
        .toPairs()
        .sortBy(sortGroup)
        .tap(ms => sortOrder > 0 ? ms : _.reverse(ms))
        .value()

    // console.log({mas: renewals.map(augmentRenewal)})
    return <>
        <div className="grid gric-cols-[auto_fr_auto_auto] gap-x-6 gap-y-4">
            {/* Header */}
            <div className="grid grid-cols-subgrid col-span-4 border-b-2 border-pcx-300">
                {headers.map((h, i) =>
                    h.key 
                        ? <div key={h.key ?? i} className={clsx(
                        "py-2 xpx-3 text-sm text-pcx-600 font-semibold uppercase tracking-wider",
                        "first:pl-4 last:pr-4",
                        h.className && h.className,
                    )}>{h.label} {h.button}</div>
                    : <div key={i} />
                )}
            </div>
            <div className="hidden last:flex flex-row gap-4 items-center p-4 bg-white rounded-xl shadow-lg border border-pcx-200 col-span-4">
                {isLoading
                    ? <div className="w-12 h-12 p-2 text-pcx-400">
                        <IconSpinner className="w-8 h-8 animate-spin" />
                    </div>
                    : <div className="w-12 h-12 p-2 rounded-xl bg-pcx-400 text-white">
                        <RocketLaunchIcon className="w-8 h-8" />
                    </div>}
                <div className="text-2xl text-pcx-500">
                    {t('no-open-instructions')} 
                    <Link to="../history" className="text-slate-600 ml-4 text-lg">
                        <ArrowRightStartOnRectangleIcon className="inline mr-1.5 h-6 w-6 mb-0.5" />
                        <span className="underline">{t('decided-maintenances')}</span>
                    </Link>
                </div>
            </div>

            {
                (groupBy === 'by-family-member' && maintenanceActions?.length > 0)
                ? <TableCard>
                        <BulkEditRow {...{renewals: maintenanceActions[0][1]}} />
                        {maintenanceActions?.flatMap(([key, actions], i) =>
                            actions.map(renewal =>
                                <RenewalRow key={renewal.id} {...{ renewal }} />)
                        )}
                    </TableCard>
                : groupBy === 'by-patent-family'
                ? maintenanceActions?.map(([key, actions], i) => {
                    const family = familyById[actions[0]?.member?.patentFamilyId]

                    if (!family) return null
                    return (
                        <TableCard key={key}>
                            <FamilyHeader {...{ family }} />
                            <BulkEditRow {...{renewals: actions}} />
                            {actions.map(renewal =>
                                <RenewalRow key={renewal.id} {...{ renewal }} />)
                            }
                            <FamilyFooter {...{ family, inDetail: actions.map(a => a.member?.familyMemberId).filter(Boolean) }} />
                        </TableCard>
                    )
                })
                // by claim scopes
                : maintenanceActions?.flatMap(([key, actions], i) => {
                    const family = familyById[actions[0]?.member?.patentFamilyId]
                    if (!family) return null

                    const { onlyFamily, claimScopes } = createClaimScopeGroups(actions, claimScopeIdsByMemberId)
                    
                    return [
                        <div key={family.patentFamilyId} className={clsx("col-span-4 grid grid-cols-subgrid pl-2 max-w-full", i > 0 && "mt-10")}>
                            <FamilyHeader key={key} {...{family}} />
                            <FamilyFooter {...{family, inDetail: actions.map(a => a.member?.familyMemberId).filter(Boolean)}} />
                        </div>,
                        onlyFamily.length > 0 ? <TableCard key={family.patentFamilyId + '-only'}>
                            <BulkEditRow {...{renewals: onlyFamily}} />
                            {onlyFamily.map((renewal: AugmentedRenewal) =>
                                <RenewalRow key={renewal.id} {...{ renewal }} />)
                            }
                        </TableCard> : null,
                        ..._(claimScopes).toPairs()
                            .map(([k, { claimScopeIds, members }], borderIndex) => {
                                return (
                                    <TableCard key={family.patentFamilyId + '-' + borderIndex} indent>
                                        <ClaimScopeHeader key={k} {...{ claimScopeIds, family, claimScopeGroupIndex: borderIndex }} />
                                        <BulkEditRow {...{renewals: members}} />
                                        {members.map(renewal =>
                                            <RenewalRow indent key={renewal.id} {...{ renewal }} />)}
                                    </TableCard>
                                )
                        }).value()
                    ]
                })
            }

        </div>
        <Outlet />
    </>
}

function ToggleDetailsView() {
    const {smallView, setSmallView} = useRenewalsFilter()

    return (
        <button onClick={() => setSmallView(!smallView)}>{
            smallView 
                ? <ArrowsPointingOutIcon className="size-4 mt-1" />
                : <ArrowsPointingInIcon className="size-4 mt-1" />
        }</button>
    )
}

function RenewalRow({renewal, indent = false}: {indent?: boolean, renewal: AugmentedRenewal}) {
    const {t} = useTranslation()

    const {member, fee: fees, status} = renewal

    return (
        <div className="col-span-4 grid grid-cols-subgrid gap-x-6 px-2 pt-1.5 mt-1.5 first:pt-0 first:mt-0 border-t-2 first:border-t-0 border-pcx-200">
            <div>
                <Link to={memberUrl(member)} className="underline-link text-lg">
                    {member.internalReference}
                </Link>
                <div className="flex flex-row gap-1">
                    <ExpiryRibbon {...{member}} />
                    <MemberRibbons {...{member}} />
                </div>
            </div>
            <div>
                <div className="flex flex-row gap-2 items-baseline">
                    <InstructionDueDate {...{renewal, status}} />
                </div>
                <div className="-ml-2 whitespace-nowrap">
                    <StatusView {...{renewal, status}} />
                </div>
            </div>
            <div className="flex flex-col items-end w-32" title={`${renewal.feeDueDate} ${t('renewal-fee-due')}`}>
                {_(fees)
                    .toPairs()
                    .map(([currency, amount]) =>
                        <FeeAmount key={currency} {...{ amount, currency }} />)
                    .value()}
                {renewal.annuity &&
                    <div className="whitespace-nowrap text-sm text-slate-600">{t('annuity')}: {renewal.annuity}</div>}
            </div>
            <div className="text-right self-center xpt-1.5">
                <InstructionLink {...{status, renewal}} />
            </div>
        </div>
    )
}

function InstructionLink({status, renewal}: {status: DueDateStatus, renewal?: Renewal}) {
    const {t} = useTranslation()

    return canBeInstructed({status, renewal}) 
        ? <Link className="btn-secondary text-base w-fit h-fit" to={`instruct/${renewal.id}`}>
            {t('instruct')}
        </Link>
        : null
}